import React from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import BaseDetailsContainer from '../../baseContainers/BaseDetailsContainer';
import CompanyService from '../../services/CompanyService';
import StepperComponent from '../../components/StepperComponent';
import ImageComponent from '../../components/inputs/ImageComponent';
import LocationsComponentV2 from '../../components/locations/LocationComponentV2';
import ActionButton from '../../components/ActionButton';
import AppPrefixUtils from '../../utils/AppPrefixUtils';
import SettingService from '../../services/SettingService';
import { CustomMessages } from '../../components/CustomMessages';
import BlockUi from '../../components/waitPanel/BlockUi';

class CompanySetupWizardContainer extends BaseDetailsContainer {
    constructor(props) {
        super(props, new CompanyService());
        this.state = {
            loading: true,
            element: {
                translations: [],
                locations: [],
            },
            imgMaxSizeParameter: undefined,
            fileSizeToTranslation: undefined,
        };
        this.settingService = new SettingService();
    }

    initBeforeSetElement() {
        this.settingService
            .getImageSize()
            .then((imgMaxSizeParameter) => {
                this.setState({
                    imgMaxSizeParameter,
                    fileSizeToTranslation: imgMaxSizeParameter * 0.001 + this.props.t('global.maxFileSizeUnit'),
                });
            })
            .catch((err) => {
                this.showErrorMessage(err.message);
            });
    }

    setElement() {
        this.service
            .get()
            .then((data) => {
                this.setState({ loading: false }, () => this.updateElement(data));
            })
            .catch((err) => this.handleGetDetailsError(err));
    }

    updateElement(data) {
        this.setState({ element: data }, () => this.initAfterSetElement());
    }

    initAfterSetElement() {
        this.unblockUi();
    }

    prepareElementBeforeRequest(element) {
        const prepared = { ...element };
        prepared.imageChanged = false;
        if (!prepared.logoUuid) {
            prepared.imageChanged = true;
        }
        if (!prepared.logoDarkUuid) {
            prepared.imageDarkChanged = true;
        }
        return prepared;
    }

    handleSave = () => {
        const request = this.prepareElementBeforeRequest(this.state.element);
        this.blockUi();
        this.service
            .update(request)
            .then(() => {
                this.unblockUi();
                this.persistMessage('success', '', this.props.t('global.saved'));
                this.props.history.replace(AppPrefixUtils.locationHrefUrl('/company/details'));
            })
            .catch((err) => {
                this.unblockUi();
                this.showErrorMessage(err?.message || 'Nie udało się zapisać');
            });
    };

    hasPrimaryLogo() {
        return !!this.state.element?.logoUuid || !!this.state.element?.newLogoUuid;
    }

    hasAnyLocation() {
        return Array.isArray(this.state.element?.locations) && this.state.element.locations.length > 0;
    }

    render() {
        const { t } = this.props;
        const items = [{ label: t('company.logo') }, { label: t('my-company.locations') }];

        return (
            <div className='container-fluid'>
                <BlockUi tag='div' blocking={this.state.blocking || this.state.loading} loader={this.loader} rendered>
                <div className='row'>
                    <div className='col-12'>
                        <h4 className='mb-3'>{t('company.details')} – {t('global.wizard')}</h4>
                    </div>
                    <div className='col-12'>
                        <div className='card mb-3' style={{border: '1px solid #E6E8EB'}}>
                            <div className='card-body'>
                                <h5 className='card-title mb-2'>{t('company.setupWizard.introTitle')}</h5>
                                <p className='mb-2 text-muted'>{t('company.setupWizard.introBody')}</p>
                                <ul className='mb-0 text-muted' style={{paddingLeft: '18px'}}>
                                    <li>{t('company.setupWizard.bullets.logo')}</li>
                                    <li>{t('company.setupWizard.bullets.locations')}</li>
                                </ul>
                                <div className='mt-2 text-muted'>{t('company.setupWizard.cta')}</div>
                            </div>
                        </div>
                    </div>
                    <div className='col-12'>
                        <CustomMessages id='custom-messages' ref={(el) => (this.messages = el)}></CustomMessages>
                        <StepperComponent
                            outsideClassName
                            stepClassName={'mb-3'}
                            model={items}
                            readOnly={true}
                            buttonsAlignRight
                            renderRightExtras={(activeStep, isLast) => {
                                if (isLast) {
                                    return (
                                        <ActionButton
                                            id={'company-setup-finish'}
                                            label={t('global.save')}
                                            className={'save-btn'}
                                            handleClick={this.handleSave}
                                            disabled={!this.hasPrimaryLogo() || !this.hasAnyLocation()}
                                        />
                                    );
                                }
                                return null;
                            }}
                            components={[
                                // Step 1: Logo
                                <div className='row' key='step-logo'>
                                    <div className='col-xl-6 col-lg-6 col-md-6 col-sm-12'>
                                        <ImageComponent
                                            id='company-logo'
                                            style={{ maxWidth: '80%', maxHeight: '400px', height: 'auto', width: 'auto' }}
                                            label={t('company.logo')}
                                            key='company-logo'
                                            srcUrl={`${this.service.getPath()}/logo/${this.state.element?.logoUuid}?token=${this.authService?.getToken()}`}
                                            alt='Logo'
                                            handleRemove={() => {
                                                this.setState((prevState) => ({
                                                    element: { ...prevState.element, logoUuid: null },
                                                }));
                                            }}
                                            notValidImageTypeErrorMessage={() => this.showErrorMessage(t('image.notValidImageType'))}
                                            validator={this.validator}
                                            errorMessage={this.showErrorMessage}
                                            imgMaxSizeParameter={this.state.imgMaxSizeParameter}
                                            fileSizeToTranslation={this.state.fileSizeToTranslation}
                                            attachment={this.state.element.logoUuid}
                                            setFile={(uploadedFile) => {
                                                if (uploadedFile) {
                                                    this.setState((prevState) => ({
                                                        element: { ...prevState.element, newLogoUuid: uploadedFile.uuid },
                                                    }));
                                                }
                                            }}
                                            viewMode={'EDIT'}
                                        />
                                    </div>
                                    <div className='col-xl-6 col-lg-6 col-md-6 col-sm-12'>
                                        <ImageComponent
                                            id='company-logo-dark'
                                            label={t('company.logo-dark')}
                                            key='company-logo-dark'
                                            srcUrl={`${this.service.getPath()}/logo/${this.state.element?.logoDarkUuid}?token=${this.authService?.getToken()}`}
                                            alt='Logo-dark'
                                            handleRemove={() => {
                                                this.setState((prevState) => ({
                                                    element: { ...prevState.element, logoDarkUuid: null },
                                                }));
                                            }}
                                            notValidImageTypeErrorMessage={() => this.showErrorMessage(t('image.notValidImageType'))}
                                            validator={this.validator}
                                            errorMessage={this.showErrorMessage}
                                            imgMaxSizeParameter={this.state.imgMaxSizeParameter}
                                            fileSizeToTranslation={this.state.fileSizeToTranslation}
                                            attachment={this.state.element.logoDarkUuid}
                                            setFile={(uploadedFile) => {
                                                if (uploadedFile) {
                                                    this.setState((prevState) => ({
                                                        element: { ...prevState.element, newLogoDarkUuid: uploadedFile.uuid },
                                                    }));
                                                }
                                            }}
                                            viewMode={'EDIT'}
                                        />
                                    </div>
                                    <div className='col-12 mt-2'></div>
                                </div>,
                                // Step 2: Locations
                                <div className='row' key='step-locations'>
                                    <div className='col-12'>
                                        <LocationsComponentV2
                                            showErrorMessage={this.showErrorMessage}
                                            showSuccessMessage={this.showSuccessMessage}
                                            viewMode={'EDIT'}
                                            locations={this.state?.element?.locations}
                                            onAddLocation={(location) => {
                                                const locations = this.state.element.locations || [];
                                                locations.push(location);
                                                this.setState((prevState) => ({
                                                    element: { ...prevState.element, locations },
                                                }));
                                            }}
                                            validator={this.validator}
                                            onUpdateLocation={(locations) => {
                                                this.setState((prevState) => ({
                                                    element: { ...prevState.element, locations },
                                                }));
                                            }}
                                        />
                                    </div>
                                    <div className='col-12 mt-3'></div>
                                </div>,
                            ]}
                        />
                    </div>
                </div>
                </BlockUi>
            </div>
        );
    }
}

export default withRouter(withTranslation()(CompanySetupWizardContainer));


